<template>
  <div>
    <div class="row gutter-x-lg items-center">
      <div class="col-sm-auto col-12 gt-xs">
        <div class="bg-grey-1 circle text-center q-mr-lg" style="height: 125px; width: 125px; line-height: 125px;">
          <q-icon name="local_mall" color="grey-3" size="100px" class="bg-grey-1 circle"/>
        </div>
      </div>

      <div class="col-sm col-12">
        <q-field :error="errors.has('itemName')" :error-label="errors.first('itemName')">
          <q-input
            ref="itemName"
            v-model="formData.itemName"
            v-validate.disable="'required|max:150'"
            :placeholder="$t('item_name')"
            class="items-form__name"
            name="itemName"
          />
        </q-field>

        <q-field :error="errors.has('articleId')" :error-label="errors.first('articleId')">
          <q-input
            v-model="formData.itemNumber"
            v-validate.disable="'max:45'"
            :placeholder="$t('article_id')"
            class="items-form__id"
            name="articleId"
          />
        </q-field>
      </div>
    </div>

    <div class="row gutter-x-md q-mt-md">
      <div class="col-12 col-md-6">
        <q-field :error="errors.has('price')" :error-label="errors.first('price')">
          <q-input
            @input="priceUpdate"
            v-model="formData.price"
            v-validate.disable="'required'"
            :placeholder="$t('price_net')"
            class="items-form__price"
            name="price"
          />
        </q-field>
      </div>

      <div class="col-12 col-md-6">
        <q-field :error="errors.has('salesTax')" :error-label="errors.first('salesTax')">
          <sales-tax-input
            v-model="formData.salesTax"
            @input="handleTaxUpdate"
            :use-default="!isUpdate"
            class="items-form__sales-tax"
            required
          />
        </q-field>
      </div>

      <div class="col-12 col-md-6">
        <q-field :error="errors.has('defaultQuantity')" :error-label="errors.first('defaultQuantity')">
          <q-input
            v-model="formData.defaultQuantity"
            v-validate.disable="'required'"
            :placeholder="$t('default_quantity')"
            class="items-form__defaultQuantity"
            name="defaultQuantity"
          />
        </q-field>
      </div>

      <div class="col-12 col-md-6">
        <q-field :error="errors.has('unitName')" :error-label="errors.first('unitName')">
          <q-input
            v-model="formData.unitName"
            v-validate.disable="'required|max:45'"
            :placeholder="$t('unit_name')"
            class="items-form__unitName"
            name="unitName"
          >
            <q-autocomplete :static-data="{ field: 'label', list: defaultUnits }" :min-characters="0"/>
          </q-input>
        </q-field>
      </div>
    </div>

    <div class="q-mt-lg q-mb-sm">
      <q-input
        type="textarea"
        :placeholder="$t('description')"
        v-model="formData.description"
        rows="3"
      />
    </div>
  </div>
</template>

<script>
  import Decimal from 'decimal.js'
  import SalesTaxInput from 'components/Common/Inputs/SalesTaxInput.vue'
  import changeCase from 'change-case-object'
  import { createErrorFromResponse } from 'src/utils/error'
  import { items } from 'src/config'
  import { mapActions } from 'vuex'

  const initialState = () => {
    return {
      itemName: null,
      itemNumber: null,
      salesTax: null,
      price: null,
      currency: 'EUR',
      defaultQuantity: null,
      unitName: null,
      description: null,
      priceGross: null
    }
  }

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        formData: initialState()
      }
    },

    props: {
      item: {
        type: Object,
        required: false
      }
    },

    /**
     * The item prop is watched so the UI gets updated whenever a new item is provided.
     */
    watch: {
      item (data) {
        data.price = new Decimal(data.price).div(100)
        this.formData = data
        this.priceUpdate(data.price)
      }
    },

    computed: {
      defaultUnits () {
        return items.units.map(unit => ({ value: this.$tc(unit), label: this.$tc(unit) }))
      },

      isUpdate () {
        return this.item && typeof this.item.id === 'number'
      }
    },

    methods: {
      ...mapActions(['createItem', 'updateItem']),

      create () {
        return this.$validator.validateAll()
          .then(result => {
            if (!result) {
              throw new Error('validation failed')
            }

            let data = changeCase.snake(this.formData)
            data.price = this.priceInCent(this.formData.price)
            data.default_quantity = this.replaceDecimalSign(this.formData.defaultQuantity)

            return this.createItem(data)
          })
          .then(response => {
            this.$q.notify({ message: this.$t('item_created'), type: 'positive' })

            return true
          })
          .catch(error => {
            if (!error.response) {
              return
            }

            createErrorFromResponse(error)

            return false
          })
      },

      update () {
        if (!this.isUpdate) throw new Error('No item to update provided')

        let data = changeCase.snake(this.formData)
        data.price = this.priceInCent(this.formData.price)
        data.default_quantity = this.replaceDecimalSign(this.formData.defaultQuantity)

        return this.$validator.validateAll()
          .then(result => {
            if (!result) {
              throw new Error('validation failed')
            }

            return this.updateItem({ itemId: this.item.id, item: data })
          })
          .then(response => {
            this.$q.notify({ message: this.$t('item_updated'), type: 'positive' })

            return true
          })
          .catch(error => {
            if (!error.response) {
              return
            }

            createErrorFromResponse(error)

            return false
          })
      },

      replaceDecimalSign (value) {
        if (typeof value === 'string') {
          value = value.replace(/,/, '.')
        }

        return value
      },

      priceInCent (price) {
        price = this.replaceDecimalSign(price)

        return new Decimal(price).times(100).round()
      },

      grossUpdate (price) {
        if (!price || !this.formData.salesTax) return
        let salesTax = new Decimal(this.formData.salesTax).div(100).add(1)

        price = this.priceInCent(price)
        this.formData.price = price.div(salesTax).round().div(100)
      },

      priceUpdate (price) {
        if (!price || !this.formData.salesTax) return
        let salesTax = new Decimal(this.formData.salesTax).div(100).add(1)

        price = this.priceInCent(price)
        this.formData.priceGross = price.times(salesTax).round().div(100)
      },

      handleTaxUpdate (tax) {
        if (!tax || !this.formData.price) return
        let price = this.priceInCent(this.formData.price)

        tax = new Decimal(tax).div(100).add(1)
        this.formData.priceGross = price.times(tax).round().div(100)
      },

      reset () {
        this.formData = initialState()
      },

      focus () {
        this.$refs.itemName.focus()
      }
    },

    components: {
      SalesTaxInput
    }
  }
</script>
